import { useContext, useCallback, useMemo, useState } from 'react';
import DomPurify from 'dompurify';
import { nanoid } from 'nanoid';
import { UserContext } from 'context/User/UserContext';
import { ThemeProvider } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { Button } from '@upmc/ims.component-library';
import fileDownload from 'js-file-download';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import FileUpload from 'components/UI/Input/FileUpload/FileUpload';
import { DataTable } from 'components/UI/MDC/DataTable/DataTable';
import { ACTION_TYPES } from 'components/UI/MDC/DataTable/_types';
import { theme } from '../../../../components/layouts/theme';
import { HelpBar } from './HelpBar';
import { getBulkUploadTemplate, postBulkUpload } from '../_api';
import {
	BULK_UPLOAD_HEADERS,
	BULK_UPLOAD_MAX,
	CLASS_SPEED_DIAL,
} from '../_const';

/**
 * Complex object generated by successful file upload parsing.
 *
 * @typedef {Object} File
 * @property {string} file.fileName name of uploaded file.
 * @property {string[]} file.fileHeaders array of column headers.
 * @property {string[]} file.fileContent comma-separated row cell values.
 */

/**
 * Epic Bulk Upload Form for creating or editing user job category assignments.
 * @returns Bulk Upload page view
 */
const EditorView = () => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const maintenanceUser = contextUser.userInfo.userProfile;

	const [sAlert, setAlert] = useState({ text: '', color: '' });
	const [sArrInvalid, setArrInvalid] = useState([]);
	const [sDrawerOpen, setDrawerOpen] = useState(true);
	const [sLoading, setLoading] = useState(false);
	const [sSpeedDialOpen, setSpeedDialOpen] = useState(false);

	const arrHeaders = useMemo(
		() => [
			{
				Value: 'Report ID',
			},
			{
				Value: 'Old JC ID',
			},
			{
				Value: 'JC ID',
			},
			{
				Value: 'Experienced',
			},
			{
				Value: 'Validated',
			},
			{
				Value: 'Wave',
			},
		],
		[]
	);

	/**
	 * Event handler raised when file successfully uploaded.
	 * @param {File} file parsed file object.
	 * @returns void
	 */
	const onFileUpload = async file => {
		setLoading(true);
		setArrInvalid([]);

		try {
			const valid = validateFile(file);
			if (valid) {
				const arrInvalid = await postBulkUpload(maintenanceUser.ID, [
					file.fileHeaders.join(','),
					...file.fileContent,
				]);
				setArrInvalid(generateRows(arrInvalid));
				if (arrInvalid.length > 0) {
					setAlert({
						text: 'Some rows could not be processed.',
						color: 'warning',
					});
				} else {
					setAlert({
						text: 'File successfully uploaded.',
						color: 'success',
					});
				}
			}
		} catch (ex) {
			setAlert({
				text: ex,
				color: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	const generateRows = payload => {
		return payload.map(row => {
			row.ActionCells = [
				{
					BindTo: 0,
					Text: 'Submit',
				},
			];
			row.Key = nanoid();

			return row;
		});
	};

	/**
	 * Performs basic validation on uploaded file.
	 * @param {File} file
	 * @returns {boolean} validation state.
	 */
	const validateFile = file => {
		let result = false;
		if (
			!file.fileHeaders.some(
				header =>
					header.toLowerCase() ===
					BULK_UPLOAD_HEADERS.REPORT_ID.toLowerCase()
			) ||
			!file.fileHeaders.some(
				header =>
					header.toLowerCase() ===
					BULK_UPLOAD_HEADERS.JC.toLowerCase()
			) ||
			!file.fileHeaders.some(
				header =>
					header.toLowerCase() ===
					BULK_UPLOAD_HEADERS.WAVE.toLowerCase()
			)
		) {
			setAlert({
				type: 'error',
				text: 'At least one required column header is missing.',
			});
		} else if (file.fileContent.length > BULK_UPLOAD_MAX) {
			setAlert({
				type: 'error',
				text: `File contains too many rows. Please limit to ${BULK_UPLOAD_MAX}.`,
			});
		} else {
			result = true;
			setAlert({ type: '', text: '' });
		}

		return result;
	};

	/**
	 * Memoized event handler to update state in response to validation cell change.
	 */
	const onCellChangedHandler = useCallback((e, row, key, arrRows) => {
		let arrRowsCopy = [...arrRows];
		const index = arrRowsCopy.indexOf(row);
		arrRowsCopy[index].Cells[key].Value = e.target.value;
		setArrInvalid(arrRowsCopy);
	}, []);

	/**
	 * Memoized event handler to attempt re-submission of invalid row. Row removed from local state if row found to be valid.
	 */
	const onRowSubmitHandler = useCallback(
		async (rows, row) => {
			setLoading(true);
			setAlert({ type: '', text: '' });

			try {
				const headerCSV = Object.keys(BULK_UPLOAD_HEADERS).reduce(
					(prev, curr, index) => {
						return (
							prev +
							(index > 0 ? ',' : '') +
							BULK_UPLOAD_HEADERS[curr]
						);
					},
					''
				);
				const rowCSV = Object.keys(row.Cells).reduce(
					(prev, current, index) => {
						return (
							prev +
							(index > 0 ? ',' : '') +
							(DomPurify.sanitize(row.Cells[current].Value) ?? '')
						);
					},
					''
				);
				const arrInvalid = await postBulkUpload(maintenanceUser.ID, [
					headerCSV,
					rowCSV,
				]);
				let arrRowsCopy = [...rows];
				const index = arrRowsCopy.indexOf(row);
				if (arrInvalid.length > 0) {
					arrRowsCopy[index] = generateRows(arrInvalid)[0];
					arrRowsCopy[index].Key = row.Key;
				} else {
					arrRowsCopy = arrRowsCopy
						.slice(0, index)
						.concat(arrRowsCopy.slice(index + 1));
					setAlert({
						text: 'Row re-submission successful.',
						type: 'success',
					});
				}
				setArrInvalid(arrRowsCopy);
			} catch (ex) {
				setAlert({
					text: ex,
					color: 'error',
				});
				setArrInvalid([]);
			} finally {
				setLoading(false);
			}
		},
		[maintenanceUser]
	);

	return (
		<ThemeProvider theme={theme}>
			<PageWithBreadcrumbs currentPageLabel={'New Bulk Upload'}>
				{sAlert.text && (
					<Alert
						sx={{
							alignItems: 'center',
							margin: '0px auto 16px auto',
							width: '45%',
							'& .MuiAlert-action': {
								paddingLeft: '0',
							},
						}}
						severity={sAlert.color}
						onClose={() => setAlert({ type: '', text: '' })}
					>
						{sAlert.text}
					</Alert>
				)}
				<WithLoadingHandler
					isLoading={sLoading}
					showChildren={!sLoading}
				>
					<Grid
						xs={12}
						container
						sx={{ flexDirection: 'column', alignItems: 'center' }}
					>
						<FileUpload
							fileTypes={'.csv'}
							fileMIMETypes={'text/csv'}
							onClickHandler={onFileUpload}
							parseRegex={new RegExp(/(\r?\n)/)}
						/>
						<Button
							sx={{ width: '16rem' }}
							id='user-upload__button--download'
							onClick={() => {
								getBulkUploadTemplate()
									.then(response =>
										fileDownload(
											response.data,
											`IMS_Bridges_Bulk_Upload_.csv`
										)
									)
									.catch();
							}}
							startIcon={<DownloadIcon />}
							variant='text'
						>
							Download Template
						</Button>
					</Grid>
				</WithLoadingHandler>
				<HelpBar open={sDrawerOpen} setOpen={setDrawerOpen} />
				{sArrInvalid.length > 0 && (
					<Box
						sx={{
							marginTop: '-48px',
							marginLeft: '64px',
							paddingBottom: '16px',
							width: {
								xs: sDrawerOpen ? '60%' : '85%',
								lg: sDrawerOpen ? '70%' : '95%',
							},
						}}
					>
						<DataTable
							actions={[
								{
									Type: ACTION_TYPES.DONE,
									onChangedHandler: (_, rows, row) =>
										onRowSubmitHandler(rows, row),
								},
							]}
							headers={arrHeaders}
							infiniteScroll
							pageSize={50}
							label='Failed Rows'
							loading={sLoading}
							sublabel={`Hover over invalid cells for more information. If desired, inline corrections can be made and re-submitted with the row's leading icon.`}
							rows={sArrInvalid}
							validation={{
								onCellChangedHandler: (e, row, key) =>
									onCellChangedHandler(
										e,
										row,
										key,
										sArrInvalid
									),
							}}
						/>
					</Box>
				)}
				<SpeedDial
					classes={CLASS_SPEED_DIAL}
					sx={{
						position: 'fixed',
						bottom: 56 + 8 + 24,
						left: 24,
					}}
					ariaLabel='Help'
					icon={
						<SpeedDialIcon
							icon={
								<MoreVertIcon
									key={'fab--help'}
									fontSize='medium'
								/>
							}
							openIcon={<CloseIcon />}
						/>
					}
					onClick={() => setSpeedDialOpen(isOpen => !isOpen)}
					open={sSpeedDialOpen}
				>
					<SpeedDialAction
						classes={CLASS_SPEED_DIAL}
						key='0'
						icon={<QuestionMarkIcon color='secondary' />}
						tooltipTitle='FAQ'
						tooltipPlacement='right'
						onClick={() => setDrawerOpen(open => !open)}
					/>
				</SpeedDial>
			</PageWithBreadcrumbs>
		</ThemeProvider>
	);
};

export default EditorView;
